import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { FormSettingsEnum, PreviewPageType } from '@dev/ee-api/ee-api-models';
import { SimplePopover, SecondaryButton, Typography, palette } from '@dev/ee-components';
import { Link } from '@material-ui/core';
import { isEmpty } from 'lodash-es';

import { getFormCreatorStore, LoadPageTemplateStart } from '@/store/formCreator/actions';
import { StartLoadingTemplateParams } from '@/store/landingPageList/actions';
import OpenInNew from '@/services/icons/open-in-new-icon.svg';
import { useSecurity } from '@/services/hooks/useSecurity';
import { links } from '@/services/dictionaries/links';

import { TemplatesList } from '../../FormStep3Interfaces';
import { useLandingPageList } from '../../hooks/useLandingPageList';
import { LandingPageSection } from './LandingPageSection';
import { FormPageDropdownWrapper, DisplayedScreenLine, ScreenDropdownWrapper, OpenInNewWrapper, NoDataWrapper } from '../styles';
import { ExternalWebsiteInput } from '../ExternalWebsiteInput';

export enum DisplayedScreenEnum {
    Defaultpage = 'defaultpage',
    Landingpage = 'landingpage',
    Externalwebsite = 'externalwebsite',
}

interface FormPageDropdownProps {
    label: string;
    idPageDropdown: string;
    onDropdownPageClick: (ev, obj) => void;
    pageType: DisplayedScreenEnum;
    idExternalWebsiteInput: string;
    externalWebsiteValue: string;
    setExternalWebsite?: (externalWebsiteValue: string) => void;
    isExternalWebsiteError: boolean;
    setExternalWebsiteError: (isExternalWebsiteError: boolean) => void;
    pageTemplateType: PreviewPageType;
    idLandingPage: string;
    chosenLandingPage: TemplatesList;
    setChosenLandingPage: (chosenLandingPage: TemplatesList) => void;
    landingPageSearch: string;
    setLandingPageSearch: (landingPageSearch: string) => void;
    landingPagesParams: StartLoadingTemplateParams;
}

export const FormPageDropdownSection = (props: FormPageDropdownProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pageTemplates,formPayload } = useSelector(getFormCreatorStore);
    const { isReseller } = useSecurity();
    const landingPageList = useLandingPageList(props.pageTemplateType, props.landingPageSearch);
    const isLandingPageData = !isEmpty(landingPageList);
    const hasDoubleOptIn = formPayload?.FormSettings?.includes?.(FormSettingsEnum.AddDoubleOptIn);
    const isNoResult = landingPageList?.[0]?.value === 'noresults';
    useEffect((): void => {

        dispatch(LoadPageTemplateStart(props.pageTemplateType));
        props?.pageTemplateType === PreviewPageType.FormSubmit && dispatch(LoadPageTemplateStart(PreviewPageType.FormSubmitDoubleOptIn));
    }, [props.pageTemplateType]);

    const openPreview = (): void => {
        if (props.pageType === DisplayedScreenEnum.Defaultpage) {
            const pagePreview = window.open('', `PagePreview`);
            let pageTemplate = '';

            switch (props.pageTemplateType) {
                case PreviewPageType.ActivationViaEmail:
                    pageTemplate = pageTemplates.activationViaEmailPage;
                    break;
                case PreviewPageType.AlreadySubscribed:
                    pageTemplate = pageTemplates.alreadySubscribedPage;
                    break;
                case PreviewPageType.FormSubmit:
                case PreviewPageType.FormSubmitDoubleOptIn:

                    pageTemplate = hasDoubleOptIn ? pageTemplates.formSubmitDoubleOptInPage : pageTemplates.formSubmitPage;
                    break;
                default:
                    break;
            }
            pagePreview.document.write(pageTemplate);
            pagePreview?.document?.close?.();
        } else if (props.pageType === DisplayedScreenEnum.Externalwebsite) {
            window.open(props.externalWebsiteValue, '_blank');
        } else {
            window.open(props.chosenLandingPage.link || landingPageList[0].link, '_blank');
        }
    };

    const displayedScreenOption = [
        {
            label: t('defaultpage'),
            value: DisplayedScreenEnum.Defaultpage,
        },
        {
            label: t('landingpage'),
            value: DisplayedScreenEnum.Landingpage,
        },
        {
            label: t('externalwebsite'),
            value: DisplayedScreenEnum.Externalwebsite,
        },
    ];

    return (
        <FormPageDropdownWrapper>
            <DisplayedScreenLine>
                <ScreenDropdownWrapper>
                    <SimplePopover
                        type="input"
                        id={`formCreator-step3-input-display-screen-${props.idPageDropdown}`}
                        label={props.label}
                        onItemClick={props.onDropdownPageClick}
                        value={props.pageType}
                        fullWidth
                        options={displayedScreenOption}
                    />
                </ScreenDropdownWrapper>
                <SecondaryButton
                    id="preview-button"
                    text={t('preview')}
                    endIcon={
                        <OpenInNewWrapper>
                            <OpenInNew />
                        </OpenInNewWrapper>
                    }
                    onClick={openPreview}
                    disabled={
                        (props.pageType === DisplayedScreenEnum.Externalwebsite &&
                            (props.isExternalWebsiteError || !props.externalWebsiteValue)) ||
                        (props.pageType === DisplayedScreenEnum.Landingpage && (!isLandingPageData || isNoResult))
                    }
                />
            </DisplayedScreenLine>
            {props.pageType === DisplayedScreenEnum.Landingpage && isLandingPageData && (
                <LandingPageSection
                    idLandingPage={props.idLandingPage}
                    pageTemplateType={props.pageTemplateType}
                    chosenLandingPage={props.chosenLandingPage}
                    setChosenLandingPage={props.setChosenLandingPage}
                    landingPageSearch={props.landingPageSearch}
                    setLandingPageSearch={props.setLandingPageSearch}
                    landingPagesParams={props.landingPagesParams}
                />
            )}
            {props.pageType === DisplayedScreenEnum.Landingpage && !isLandingPageData && (
                <NoDataWrapper>
                    <Typography variant="body1">
                        {t('youneedtocreatelandingfirst')}{' '}
                        {!isReseller && (
                            <Link target="_blank" style={{ color: palette.indigo[6] }} href={links.manageLandingPage}>
                                {t('learnmore')}
                            </Link>
                        )}
                    </Typography>
                </NoDataWrapper>
            )}
            {props.pageType === DisplayedScreenEnum.Externalwebsite && (
                <ExternalWebsiteInput
                    value={props.externalWebsiteValue} 
                    id={props.idExternalWebsiteInput}
                    pageTemplateType={props.pageTemplateType} 
                    setExternalWebsite={props.setExternalWebsite} 
                    setError={props.setExternalWebsiteError} 
                    isError={props.isExternalWebsiteError}
                />
            )}
        </FormPageDropdownWrapper>
    );
};
